import styled from '@emotion/styled'
import { MoreOutlined, HolderOutlined } from '@ant-design/icons'
import { Dropdown, Input } from 'antd'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import colors from '@sportninja/common/constants/appColors'
import { CSSProperties, useState } from 'react'
import Text from 'src/components/ts-components/Text'
import { paragraphSmall } from 'src/components/css'

type Props = {
  clientId: string
  name: string
  onRename: (newName: string) => void
  onDelete: () => void
}

const DivisionRow = ({ clientId, name, onRename, onDelete }: Props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: clientId })

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const commit = () => {
    const trimmed = draft.trim()
    if (trimmed) {
      onRename(trimmed)
    }
    setEditing(false)
  }

  return (
    <Row ref={setNodeRef} style={style}>
      <DragHandle {...attributes} {...listeners} aria-label='Drag handle'>
        <HolderOutlined />
      </DragHandle>
      <Body>
        {editing ? (
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onPressEnter={commit}
            data-testid='division-rename-input'
          />
        ) : (
          <>
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              {name}
            </Text>
            <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
              Division
            </Text>
          </>
        )}
      </Body>
      <Dropdown
        trigger={['click']}
        menu={{
          items: [
            { key: 'edit', label: 'Edit', onClick: () => setEditing(true) },
            { key: 'delete', label: 'Delete', danger: true, onClick: onDelete },
          ],
        }}
      >
        <MenuButton type='button' aria-label='Division menu'>
          <MoreOutlined />
        </MenuButton>
      </Dropdown>
    </Row>
  )
}

export default DivisionRow

// ---- styled ----
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: ${colors.SECONDARY_300};
  border-radius: 8px;

  .ant-input {
    ${paragraphSmall};
    color: ${colors.WHITE} !important;
  }
`

const DragHandle = styled.span`
  cursor: grab;
  color: ${colors.NEUTRAL_50};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`

const Body = styled.div`
  flex: 1;
  min-width: 0;
`

const MenuButton = styled.button`
  background: transparent;
  border: 0;
  color: ${colors.PRIMARY};
  cursor: pointer;
  font-size: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`
